import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { resolveRosterAccessibleSections } from "@/features/roster-activity";
import { NavLink } from "@/layouts/AppLayout/types";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import { Path } from "@/router";
import useCapabilities, { useCanViewCommitteesBool, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { Section } from "@/types";
import { ALL_SECTIONS_QUERY } from "../queries";
import { SECTION_NAV_QUERY } from "./queries";

type GetSectionNavLinksOptions = {
  sectionOfficeFacilityId?: string | null;
  canViewFacilities?: boolean;
  canViewCommittees?: boolean;
  canManageNotificationEmails?: boolean;
  governanceEnabled?: boolean;
};

export const SECTION_NAV_PEOPLE_LABEL = "People";
export const SECTION_OFFICE_LABEL = "Section Office";
export const VIEW_SECTION_OFFICE_ACTION_LABEL = "View Section Office";
export const SECTION_NAV_ALL_SECTIONS_LABEL = "All Sections";

export type SectionProfileHomePath = "/sections/:sectionId/people/section-people" | "/sections/:sectionId/jobs";

export const SECTION_PEOPLE_PATH = "/sections/:sectionId/people" as const;
export const SECTION_PEOPLE_TABLE_PATH: SectionProfileHomePath = "/sections/:sectionId/people/section-people";
export const SECTION_PEOPLE_CHANGE_REQUESTS_PATH = "/sections/:sectionId/people/change-requests" as const;
export const SECTION_PEOPLE_CHANGE_REQUESTS_SETTINGS_PATH =
  "/sections/:sectionId/people/change-requests/settings" as const;
export const SECTION_CHANGE_REQUESTS_PATH = "/sections/:sectionId/change-requests" as const;
export const SECTION_JOBS_PATH: SectionProfileHomePath = "/sections/:sectionId/jobs";

export function getSectionProfileHomePath(canViewPeople: boolean): SectionProfileHomePath {
  return canViewPeople ? SECTION_PEOPLE_TABLE_PATH : SECTION_JOBS_PATH;
}

export function getSectionOperationsFallbackPath(
  sectionId: string,
  {
    showCommittees = false,
    showSectionOfficeFacilityTabs = false,
    showGovernance = false,
    showNotifications = false
  }: {
    showCommittees?: boolean;
    showSectionOfficeFacilityTabs?: boolean;
    showGovernance?: boolean;
    showNotifications?: boolean;
  } = {}
): string {
  if (showCommittees) {
    return `/sections/${sectionId}/operations/committees`;
  }
  if (showSectionOfficeFacilityTabs) {
    return `/sections/${sectionId}/operations/employees`;
  }
  if (showGovernance) {
    return `/sections/${sectionId}/operations/governance`;
  }
  if (showNotifications) {
    return `/sections/${sectionId}/operations/notifications`;
  }
  return `/sections/${sectionId}/jobs`;
}

export function getSectionOperationsVisibility({
  sectionOfficeFacilityId,
  canViewFacilities = false,
  canViewCommittees = false,
  canManageNotificationEmails = false,
  governanceEnabled = false
}: GetSectionNavLinksOptions = {}) {
  const showCommittees = Boolean(canViewCommittees);
  const showSectionOfficeFacilityTabs = Boolean(sectionOfficeFacilityId && canViewFacilities);
  const showNotifications = Boolean(canManageNotificationEmails);
  const showGovernance = Boolean(governanceEnabled);

  return {
    showCommittees,
    showSectionOfficeFacilityTabs,
    showNotifications,
    showGovernance,
    showOperations: showCommittees || showSectionOfficeFacilityTabs || showNotifications || showGovernance
  };
}

export const getSectionNavLinks = (
  sectionId: string,
  {
    sectionOfficeFacilityId,
    canViewFacilities = false,
    canViewCommittees = false,
    canManageNotificationEmails = false,
    governanceEnabled = false
  }: GetSectionNavLinksOptions = {}
): Record<string, NavLink<Path>[]> => {
  const showSectionOfficeLink = Boolean(sectionOfficeFacilityId && canViewFacilities);
  const { showOperations } = getSectionOperationsVisibility({
    sectionOfficeFacilityId,
    canViewFacilities,
    canViewCommittees,
    canManageNotificationEmails,
    governanceEnabled
  });

  const jobsLink = {
    domain: DOMAIN.SECTIONS,
    label: "Jobs",
    link: {
      to: SECTION_JOBS_PATH,
      params: { sectionId }
    },
    icon: "business_center" as const
  };

  const billingLink = {
    domain: DOMAIN.SECTIONS,
    label: "Billing",
    link: {
      to: "/sections/:sectionId/billing/" as Path,
      params: { sectionId }
    },
    icon: "card_membership" as const
  };

  const operationsLink = showOperations && {
    domain: DOMAIN.SECTIONS,
    label: "Operations",
    link: {
      to: "/sections/:sectionId/operations/" as Path,
      params: { sectionId }
    },
    icon: "flowsheet" as const
  };

  const peopleWorkspaceLink = {
    domain: DOMAIN.SECTIONS,
    label: SECTION_NAV_PEOPLE_LABEL,
    link: {
      to: SECTION_PEOPLE_PATH as Path,
      params: { sectionId }
    },
    icon: "group" as const,
    matchPaths: [
      SECTION_CHANGE_REQUESTS_PATH as Path,
      "/sections/:sectionId/roster-activity" as Path,
      "/sections/:sectionId/audit-log" as Path
    ]
  };

  const sectionOfficeLink = showSectionOfficeLink && {
    domain: DOMAIN.FACILITIES,
    label: SECTION_OFFICE_LABEL,
    link: {
      to: "/facilities/:facilityId" as Path,
      params: { facilityId: sectionOfficeFacilityId! }
    },
    icon: "store" as const,
    externalLink: true,
    dividerBefore: true
  };

  const allSectionsLink = {
    domain: DOMAIN.SECTIONS,
    label: SECTION_NAV_ALL_SECTIONS_LABEL,
    link: {
      to: "/sections" as Path
    },
    icon: "domain" as const,
    externalLink: true,
    dividerBefore: !showSectionOfficeLink
  };

  const sectionLinks = [
    peopleWorkspaceLink,
    jobsLink,
    billingLink,
    operationsLink,
    sectionOfficeLink,
    allSectionsLink
  ].filter(Boolean) as NavLink<Path>[];

  return {
    "Manage Section": sectionLinks
  };
};

type WithAllSectionsFlyoutOptions = {
  homeTo?: SectionProfileHomePath;
};

export function withAllSectionsFlyout(
  navLinks: Record<string, NavLink<Path>[]>,
  sections: Section[] | undefined,
  { homeTo = SECTION_JOBS_PATH }: WithAllSectionsFlyoutOptions = {}
): Record<string, NavLink<Path>[]> {
  if (!sections?.length) {
    return navLinks;
  }

  const manageSectionLinks = navLinks["Manage Section"];
  const allSectionsIndex = manageSectionLinks.findIndex((link) => link.label === SECTION_NAV_ALL_SECTIONS_LABEL);
  if (allSectionsIndex === -1) {
    return navLinks;
  }

  const flyoutLinks = [...sections]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
    .map((section) => ({
      label: section.name,
      link: {
        to: homeTo,
        params: { sectionId: section.id }
      }
    }));

  const updatedLinks = [...manageSectionLinks];
  updatedLinks[allSectionsIndex] = {
    ...manageSectionLinks[allSectionsIndex],
    trailingIcon: "navigate_next",
    flyoutLinks
  };

  return {
    ...navLinks,
    "Manage Section": updatedLinks
  };
}

export function useSectionNavLinks(sectionId?: string) {
  const flags = useFlags();
  const { hasRole, loaded: capabilitiesLoaded, sectionLegacyIds, sectionLegacyIdsLoading } = useCapabilities();
  const canViewFacilities = useHasCapabilityBool({
    domain: DOMAIN.FACILITIES,
    permission: PERMISSION.viewFacilities
  });
  const canViewSection = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.viewSection
  });
  const canViewPeople = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.viewPeople
  });
  const canViewCommittees = useCanViewCommitteesBool();
  const governanceEnabled = flags["backoffice-section-governance"] === true;
  const isSectionStaff = capabilitiesLoaded && (hasRole(ROLE.SECTION_STAFF) || hasRole(ROLE.SECTION_ADMIN));
  const shouldLoadSections = capabilitiesLoaded && canViewSection;
  const { data: sectionNavData } = useQuery(SECTION_NAV_QUERY, {
    variables: {
      sectionId: sectionId ?? ""
    },
    skip: !sectionId,
    fetchPolicy: "cache-first"
  });
  const { data: allSectionsData, loading: allSectionsLoading } = useQuery<{ sections: Section[] }>(ALL_SECTIONS_QUERY, {
    skip: !shouldLoadSections,
    fetchPolicy: "cache-first"
  });

  return useMemo(() => {
    const navLinks = getSectionNavLinks(sectionId ?? "", {
      sectionOfficeFacilityId: sectionNavData?.section?.sectionOffice?.id,
      canViewFacilities,
      canViewCommittees,
      canManageNotificationEmails: sectionNavData?.section?.permissions?.manageNotificationEmails === true,
      governanceEnabled
    });

    const sectionsStillLoading =
      (isSectionStaff && sectionLegacyIdsLoading) || (shouldLoadSections && allSectionsLoading);

    const accessibleSections = sectionsStillLoading
      ? undefined
      : resolveRosterAccessibleSections({
          canViewSection,
          isSectionStaff,
          assignedSectionIds: sectionLegacyIds,
          allSections: allSectionsData?.sections ?? []
        });

    return withAllSectionsFlyout(navLinks, accessibleSections, {
      homeTo: getSectionProfileHomePath(canViewPeople)
    });
  }, [
    sectionId,
    sectionNavData?.section?.sectionOffice?.id,
    canViewFacilities,
    canViewSection,
    canViewPeople,
    canViewCommittees,
    governanceEnabled,
    sectionNavData?.section?.permissions?.manageNotificationEmails,
    isSectionStaff,
    sectionLegacyIds,
    sectionLegacyIdsLoading,
    allSectionsLoading,
    shouldLoadSections,
    allSectionsData?.sections
  ]);
}
